<script setup lang="ts">

type Coordinate = { integer: string, decimals: string, raw: string };

import { JupiterButton, useJupiterProps } from '@modules/Jupiter';
import { BasicMap, type IBasicMap } from "@app/Components/Google";
import { useNotify } from '@library/Components';

const jupiterProps = useJupiterProps();
const lat = getParts( jupiterProps.gpsLat );
const lng = getParts( jupiterProps.gpsLong );
let smsBody = '';
let mapOptions = {} as IBasicMap;

if( lat != undefined && lng != undefined )
{

    smsBody = `I need help, I'm at ${ lat.integer }.${ lat.decimals }, ${ lng.integer }.${ lng.decimals }`;
    mapOptions = {
        size: { width: '100%', height: '200px' },
        center: { lat: jupiterProps.gpsLat!, lng: jupiterProps.gpsLong! },
        zoom: 15,
        showMarker: true
    } as IBasicMap;

}

/**
 * Splits a decimal number into two parts, integer & decimals.  Decimals are padded
 * to always return six digits
 * @param n 
 */
function getParts( n?: number ): Coordinate | undefined
{

    if( isNaN( n as any ) ) return undefined;

    const stringNumber = String( n );
    if( n == null || stringNumber.length == 0 || [ 'null', 'undefined' ].includes( stringNumber.toLowerCase() ) )
    {
        return undefined;
    }

    const parts: string[] = stringNumber.indexOf( '.' )
        ? stringNumber.split( '.' )
        : [ stringNumber, '0' ];
    const integer = parts[0];
    const decimals = ( parts[1] ?? '0' + '000000').substring( 0, 6 );

    return { 
        integer: integer, 
        decimals: decimals,
        raw: `${integer}.${decimals}` 
    };

}

/**
 * Copies the coordinates to the clipboard
 */
async function copyToClipboard()
{
    const data = `${ lat!.raw }, ${ lng!.raw }`;
    await navigator.clipboard.writeText( data );
    await useNotify( 'Your location was copied to the clipboard', '' );
}

</script>

<template>

    <div v-if="lat && lng"
         class="flex flex-col items-center w-full
                gap-4 lg:gap-6 3xl:gap-8">
    
        <div class="shadow-lg rounded-lg w-full ring-1 ring-white/80">
            <BasicMap v-model="mapOptions" 
                      class="dark:opacity-70 overflow-hidden rounded-lg" />
        </div>

        <div class="w-full flex flex-col gap-3 lg:gap-4 3xl:gap-6">

            <div class="grid grid-cols-2 gap-x-6 lg:gap-x-8 justify-center w-full 
                        p-4 lg:p-6 3xl:p-8
                        bg-white/[6%] rounded-lg">
                <div class="mb-4 3xl:mb-6">
                    <div class="3xl:text-2xl">Latitude</div>
                    <div class="text-2xl 3xl:text-4xl text-secondary-500 dark:text-amber-400">
                        {{ lat.integer }}<span class="text-base opacity-70">.{{ lat.decimals }}</span>
                    </div>
                </div>

                <div>
                    <div class="3xl:text-2xl">Longitude</div>
                    <div class="text-2xl 3xl:text-4xl text-secondary-500 dark:text-amber-400">
                        {{ lng.integer }}<span class="text-base opacity-70">.{{ lng.decimals }}</span>
                    </div>
                </div>

                <Button variant="outlined"
                        class="flex-1 lg:!h-11 3xl:!h-14 rounded-[8px]"
                        @click="() => copyToClipboard()">
                    <span class="3xl:text-xl">Copy</span>    
                </Button>

                <JupiterButton variant="outlined"
                               class="flex-1 lg:!h-11 3xl:!h-14 rounded-[8px]"
                               type="sms"
                               :to="{ number: '', message: smsBody }">
                   <span class="3xl:text-xl">Send</span>    
                </JupiterButton>

            </div>

        </div>

    </div>

    <div v-else 
         class="flex flex-col items-center tracking-wider pt-4 pb-8">
    
        <h1 class="text-base lg:text-lg xl:text-xl mb-4">
            Location Sharing Is Turned Off
        </h1>
        
        <p class="opacity-60 text-base lg:text-lg text-center mb-4">
            If you enable location sharing we can send your location to help you get assistance
        </p>

        <img src="/images/safety/location-disabled.png" class="w-full" />

    </div>

</template>